import React, { useState, useEffect } from 'react';
import apiClient from '../../services/apiClient';
import './AdminSettings.css';

const AdminSettings = ({ currentUser }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newUsername: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Initialize form with current user data
  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        newUsername: currentUser.username || ''
      }));
    }
  }, [currentUser]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear messages when user starts typing
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const validateForm = () => {
    if (!formData.newUsername.trim()) {
      setMessage({ type: 'error', text: 'Le nom d\'utilisateur ne peut pas être vide' });
      return false;
    }

    if (formData.newPassword && formData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre' });
      return false;
    }

    // Check for uppercase
    if (formData.newPassword && !/[A-Z]/.test(formData.newPassword)) {
      setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins une lettre majuscule' });
      return false;
    }

    // Check for lowercase
    if (formData.newPassword && !/[a-z]/.test(formData.newPassword)) {
      setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins une lettre minuscule' });
      return false;
    }

    // Check for number
    if (formData.newPassword && !/[0-9]/.test(formData.newPassword)) {
      setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins un chiffre' });
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const updateData = {
        currentPassword: formData.currentPassword,
        newUsername: formData.newUsername.trim()
      };

      // Only include password if user wants to change it
      if (formData.newPassword) {
        updateData.newPassword = formData.newPassword;
      }

      const response = await apiClient.updateCredentials(updateData);
      
      if (response.success) {
        setMessage({ 
          type: 'success', 
          text: 'Informations mises à jour avec succès. Vous devrez vous reconnecter.' 
        });
        
        // Clear form
        setFormData({
          currentPassword: '',
          newUsername: formData.newUsername,
          newPassword: '',
          confirmPassword: ''
        });

        // If password was changed, logout after a delay
        if (formData.newPassword) {
          setTimeout(() => {
            apiClient.logout();
            window.location.reload();
          }, 2000);
        }
      } else {
        setMessage({ type: 'error', text: response.message || 'Erreur lors de la mise à jour' });
      }
    } catch (error) {
      console.error('Settings update error:', error);
      
      // Try to extract error message from response
      let errorMessage = 'Erreur de connexion au serveur';
      if (error.message && error.message.includes('HTTP 400')) {
        errorMessage = 'Vérifiez les exigences du mot de passe: 8 caractères minimum, 1 majuscule, 1 minuscule, 1 chiffre';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage({ 
        type: 'error', 
        text: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-settings">
      <div className="settings-container">
        <div className="settings-header">
          <h2>
            <i className="fas fa-cog"></i>
            Paramètres Administrateur
          </h2>
          <p>Modifiez vos informations de connexion</p>
        </div>

        {currentUser && (
          <div className="current-user-info">
            <h3>
              <i className="fas fa-user-circle"></i>
              Utilisateur Actuel
            </h3>
            <div className="user-details">
              <div className="user-detail">
                <strong>Nom d'utilisateur:</strong> {currentUser.username}
              </div>
              <div className="user-detail">
                <strong>Dernière connexion:</strong> {currentUser.lastLogin ? new Date(currentUser.lastLogin).toLocaleString('fr-FR') : 'Inconnue'}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="settings-form">
          <div className="form-section">
            <h3>
              <i className="fas fa-shield-alt"></i>
              Authentification
            </h3>

            <div className="form-group">
              <label htmlFor="newUsername">
                <i className="fas fa-user"></i>
                Nom d'utilisateur
              </label>
              <input
                type="text"
                id="newUsername"
                value={formData.newUsername}
                onChange={(e) => handleInputChange('newUsername', e.target.value)}
                required
                placeholder="Votre nom d'utilisateur"
              />
            </div>

            <div className="form-divider">
              <span>Changer le mot de passe (optionnel)</span>
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">
                <i className="fas fa-key"></i>
                Nouveau mot de passe
              </label>
              <div className="password-input">
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  value={formData.newPassword}
                  onChange={(e) => handleInputChange('newPassword', e.target.value)}
                  placeholder="Nouveau mot de passe (optionnel)"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              <small>Laissez vide pour garder le mot de passe actuel</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">
                <i className="fas fa-check-circle"></i>
                Confirmer le nouveau mot de passe
              </label>
              <div className="password-input">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder="Confirmez le nouveau mot de passe"
                  disabled={!formData.newPassword}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={!formData.newPassword}
                >
                  <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>
          </div>

          {message.text && (
            <div className={`message ${message.type}`}>
              <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
              {message.text}
            </div>
          )}

          <div className="form-actions">
            <button 
              type="submit" 
              className={`save-btn ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Mise à jour...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  Enregistrer les modifications
                </>
              )}
            </button>
          </div>
        </form>

        <div className="security-info">
          <h3>
            <i className="fas fa-info-circle"></i>
            Informations de sécurité
          </h3>
          <ul>
            <li>Vous pouvez changer votre nom d'utilisateur à tout moment</li>
            <li>Le mot de passe actuel n'est plus requis pour les modifications</li>
            <li>Votre mot de passe doit contenir au moins 8 caractères</li>
            <li>Il doit inclure: une majuscule, une minuscule et un chiffre</li>
            <li>Utilisez un mot de passe unique et sécurisé</li>
            <li>Vous serez déconnecté après un changement de mot de passe</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;



